import {FC} from 'react';

interface Session {
  place: string;
  date: string | undefined;
}

interface ItemProps {
  item: {
    view_commerce_product: string;
    title: string;
    summary: string;
    field_formation_age: number[];
    field_formation_type: string;
    field_formation_shortage?: boolean;
    field_product_duration: string;
    field_price_list: string;
    field_training_center: string[];
    field_training_center_id: string[];
    field_start_date: string[];
    field_sessions: string[];
    field_store_color: string[];
    field_variation_status: boolean;
    type: string;
    summary_2: string;
  };
  type_formation: { [key: string]: string };
}

const AGE_MINOR = 0; // '- de 18 ans'
const AGE_YOUNG_ADULT = 1; // '18 à 25 ans'
const AGE_ADULT = 2; // '25 ans et +'

/**
 * Business rule (not symmetrical by design): "Jeune" requires the range to
 * stop at 25 (no adult flag). "Adulte" only requires the adult flag and
 * excludes the minor flag, so a range spanning young-adult + adult
 * (e.g. [18-25, 25+]) is still "Adulte", not "Tout public".
 */
export function getAudienceLabel(ages: number[] | undefined): string {
  if (!ages || ages.length === 0) return Drupal.t('Tout public');

  const isMinor = ages.includes(AGE_MINOR);
  const isYoungAdult = ages.includes(AGE_YOUNG_ADULT);
  const isAdult = ages.includes(AGE_ADULT);

  if ((isMinor || isYoungAdult) && !isAdult) return Drupal.t('Jeune');
  if (isAdult && !isMinor) return Drupal.t('Adulte');
  return Drupal.t('Tout public');
}

/**
 * Format a date string to dd/MM/yy.
 */
function formatDateShort(isoDate: string | undefined): string {
  if (!isoDate) return Drupal.t('A définir');
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return Drupal.t('A définir');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

/**
 * Parse raw session strings ("center_name::ISO_date") into {place, date} objects,
 * filter past dates, sort ascending.
 */
function parseSessions(rawSessions: string[] | undefined): Session[] {
  if (!rawSessions || rawSessions.length === 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return rawSessions
    .map((raw): Session => {
      const sep = raw.indexOf('::');
      if (sep === -1) return { place: raw, date: undefined };
      return {
        place: raw.substring(0, sep),
        date: raw.substring(sep + 2) || undefined,
      };
    })
    .filter(s => {
      if (!s.date) return true; // keep "A définir" sessions
      return new Date(s.date) >= today;
    })
    .sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
}

const Item: FC<ItemProps> = ({item, type_formation}) => {
  let storeStyle= {
    borderRight: `5px solid transparent`,
    paddingRight: "10px",
  };
  if (item.field_store_color && item.type === "formation_continue") {
    storeStyle = {
      borderRight: `5px solid #${item.field_store_color[0]}`,
      paddingRight: "10px",
    }
  }

  const audienceLabel = getAudienceLabel(item.field_formation_age);

  const sessions = parseSessions(item.field_sessions);
  const visibleSessions = sessions.slice(0, 3);
  const extraCount = sessions.length - visibleSessions.length;

  return (
    <a className="catalog-item" href={item.view_commerce_product} style={storeStyle}>

      <div className="catalog-item__meta-row">
        <div className="catalog-item__meta">
          <span>{audienceLabel}</span>
          {item.field_product_duration && <span>{item.field_product_duration}</span>}
          {type_formation[item.field_formation_type] && (
            <span>{type_formation[item.field_formation_type]}</span>
          )}
        </div>
        {item.field_formation_shortage ? (
          <span className="catalog-item__badge">{Drupal.t('Métier en pénurie / Fonction critique')}</span>
        ) : item.field_price_list === "free" ? (
          <span className="catalog-item__badge">{Drupal.t('Pas de frais d\'inscription')}</span>
        ) : null}
      </div>

      <h2 className="catalog-item__title">{item.title}</h2>

      <div className="catalog-item__content">
        {(item.summary_2) ? item.summary_2.substring(0, 120) + '...' : item.summary?.substring(0, 120) + '...'}
      </div>

      {visibleSessions.length > 0 && item.type === "formation_continue" && (
        <div className="catalog-item__sessions">
          <span className="catalog-item__sessions-label">{Drupal.t('Prochaine(s) session(s) :')}</span>
          {visibleSessions.map((session, index) => (
            <span key={index} className="catalog-item__session">
              {index > 0 && <span className="catalog-item__session-dot">·</span>}
              <i className="catalog-item__session-pin icon-pin" aria-hidden="true"></i>
              <span className="catalog-item__session-place">{session.place}</span>
              <span className="catalog-item__session-date">{formatDateShort(session.date)}</span>
            </span>
          ))}
          {extraCount > 0 && (
            <span className="catalog-item__session-extra">
              {`+ ${extraCount} ${Drupal.t('autres dates')}`}
            </span>
          )}
        </div>
      )}
    </a>
  );
};

export default Item;
